import { gql } from '@apollo/client';

const START_CONVERSATION = gql`
  mutation StartConversation(
    $sbuId: ID!
    $categoryId: ID!
    $message: String!
    $topic: String
    $isAvMessage: Boolean
    $messageFile: Upload
    $thumbnail: Upload
    $invitedUsernames: [String!]
    $isPublic: Boolean
    $description: String
    $slug: String
    $tagIds: [ID!]
  ) {
    newConversation(
      conversation: {
        categoryId: $categoryId
        sbuId: $sbuId
        topic: $topic
        invitedUsernames: $invitedUsernames
        isPublic: $isPublic
        description: $description
        slug: $slug
        tagIds: $tagIds
      }
      conversationMessage: {
        message: $message
        isAvMessage: $isAvMessage
        messageFile: $messageFile
        thumbnail: $thumbnail
      }
    ) {
      errors
      success
      userConversation {
        uuid
        star
        follow
        alerts
        messageCount
        starCount
        conversation {
          uuid
          startedBy {
            username
          }
          invited {
            username
          }
          participants {
            username
          }
          category {
            id
            name
          }
          topic
          startDate
          archivedDate
          archived
        }
      }
      conversationMessage {
        message
        uuid
        sent
        removedDate
        removed
        isAvMessage
        messageFrom {
          username
        }
        conversation {
          pk
        }
        thumbnail {
          url
          name
        }
        messageFile {
          name
          size
        }
      }
    }
  }
`;
const NEW_CONVERSATION_MESSAGE = gql`
  mutation NewConversationMessage(
    $message: String!
    $conversationId: ID!
    $isAvMessage: Boolean
    $messageFile: Upload
    $thumbnail: Upload
  ) {
    conversationMessage(
      conversationMessage: {
        conversationId: $conversationId
        message: $message
        isAvMessage: $isAvMessage
        messageFile: $messageFile
        thumbnail: $thumbnail
      }
    ) {
      success
      errors
      conversationMessage {
        message
        uuid
        sent
        removedDate
        removed
        isAvMessage
        messageFrom {
          username
        }
        conversation {
          pk
        }
        thumbnail {
          url
          name
        }
        messageFile {
          name
          size
        }
      }
    }
  }
`;
const JOIN_CONVERSATION = gql`
  mutation JoinConversation(
    $conversationId: ID!
    $follow: Boolean
    $star: Boolean
    $alerts: Boolean
  ) {
    joinConversation(
      conversation: { uuid: $conversationId }
      userConversation: { follow: $follow, star: $star, alerts: $alerts }
    ) {
      success
      errors
      unreadCount
      userConversation {
        uuid
        firstQuery
        latestQuery
        follow
        star
        user {
          username
        }
        tags {
          id
          name
        }
        alerts
        messageCount
        hasUpdate
        conversation {
          uuid
          conversationMessages {
            message
            uuid
            sent
            removedDate
            removed
            isAvMessage
            messageFrom {
              username
            }
            conversation {
              pk
            }
            thumbnail {
              url
              name
            }
            messageFile {
              name
              size
            }
          }
          archived
          archivedDate
          category {
            id
            description
            name
          }
          topic
          startDate
          invited {
            firstName
            lastName
            isStaff
            isHost
            isActive
            profileImageUrl
            username
            verified
          }
          startedBy {
            username
          }
          participants {
            username
            lastName
            firstName
            isActive
            isHost
            isStaff
            profileImageUrl
            verified
          }
        }
        latestMessage {
          message
          sent
          uuid
        }
      }
    }
  }
`;
const UPDATE_USER_CONVERSATION = gql`
  mutation UpdateConversation(
    $star: Boolean
    $id: ID!
    $follow: Boolean
    $alerts: Boolean
    $tagIds: [ID!] = []
    $removeTagIds: [ID!] = []
    $archived: Boolean
  ) {
    updateConversation(
      userConversation: {
        star: $star
        id: $id
        follow: $follow
        alerts: $alerts
        tagIds: $tagIds
        removeTagIds: $removeTagIds
      }
      conversation: { archived: $archived }
    ) {
      success
      errors
      unreadCount
      userConversation {
        uuid
        firstQuery
        latestQuery
        follow
        star
        user {
          username
        }
        tags {
          id
          name
        }
        alerts
        messageCount
        hasUpdate
        conversation {
          uuid
          conversationMessages {
            message
            uuid
            sent
            removedDate
            removed
            isAvMessage
            messageFrom {
              username
            }
            conversation {
              pk
            }
            thumbnail {
              url
              name
            }
            messageFile {
              name
              size
            }
          }
          archived
          archivedDate
          category {
            id
            description
            name
          }
          topic
          startDate
          invited {
            firstName
            lastName
            isStaff
            isHost
            isActive
            profileImageUrl
            username
            verified
          }
          startedBy {
            username
          }
          participants {
            username
            lastName
            firstName
            isActive
            isHost
            isStaff
            profileImageUrl
            verified
          }
        }
        latestMessage {
          message
          sent
          uuid
        }
      }
    }
  }
`;

const FILE_UPLOAD = gql`
  mutation FileUpload($file: Upload!) {
    readFile(file: $file) {
      resp
    }
  }
`;

const NEW_CHAT_MESSAGE = gql`
  mutation SendChatMessage(
    $message: String!
    $roomId: String!
    $callAttendanceId: String!
  ) {
    sendChatMessage(
      message: $message
      roomId: $roomId
      callAttendanceId: $callAttendanceId
    ) {
      __typename
      ... on ChatMessageResponse {
        __typename
        message
        success
      }
      ... on NotAuthenticatedResponse {
        __typename
        message
        success
      }
    }
  }
`;

const UPDATE_LIVE_CALL_INVITATION = gql`
  mutation UpdateLiveCallInvitation($invitationId: ID!, $rejected: Boolean!) {
    updateCallInvite(invitationId: $invitationId, rejected: $rejected) {
      success
      errors
      liveCallInvitation {
        invitationId
        accepted
        acceptedDate
        rejected
        rejectedDate
        sent
        role {
          id
          title
          description
          version
        }
        liveCall {
          uuid
          start
          end
          topic
          archived
          archivedDate
          isCancelled
          cancelledReason
          acceptedInviteCount
          rejectedInviteCount
          category {
            id
            title
            description
          }
          host {
            id
            avatar
            displayName
            preferNickname
            username
            user {
              firstName
              lastName
              isHost
              username
            }
          }
        }
        attendee: user {
          id
          avatar
          displayName
          preferNickname
          username
          user {
            firstName
            lastName
            isHost
            username
          }
        }
      }
    }
  }
`;

const ATTEND_LIVE_CALL = gql`
  mutation AttendLiveCall(
    $liveCallId: ID!
    $chatName: String!
    $hideUsername: Boolean!
    $useAvatar: Boolean!
  ) {
    attendLiveCall(
      liveCallId: $liveCallId
      chatName: $chatName
      hideUsername: $hideUsername
      useAvatar: $useAvatar
    ) {
      errors
      success
      token
      wsUrl
      liveCall {
        uuid
        topic
        start
        end
        archived
        archivedDate
        acceptedInviteCount
        rejectedInviteCount
        isCancelled
        cancelledReason
        layoutPolicy
        eventOccurrence {
          id
        }
        host {
          id
          avatar
          displayName
          preferNickname
          username
          avatar
          roles {
            name
            isActive
            id
            description
          }
        }
        callFiles {
          callFileId
          timestamp
          liveCallFile {
            name
            size
            url
            path
          }
          thumbnail {
            name
            path
            size
            url
          }
        }
      }
      liveCallAttendees {
        callAttendanceId
        start
        end
        chatName
        hideUsername
        useAvatar
        invitation {
          invitationId
          acceptedDate
          rejected
          accepted
          rejectedDate
          sent
          role {
            id
            title
            version
            description
          }
          attendee: user {
            avatar
            displayName
            preferNickname
            username
          }
        }
      }
      attendance {
        callAttendanceId
        start
        end
        chatName
        hideUsername
        invitation {
          invitationId
          acceptedDate
          rejected
          accepted
          rejectedDate
          sent
          attendee: user {
            preferNickname
            username
            displayName
            avatar
          }
          role {
            id
            title
            version
            description
          }
        }
      }
      liveCallMessages {
        currentUser
        invitationId
        attendanceId: callAttendanceId
        message
        roomId
        success
        timestamp
        topic
        isSystemMessage
        avatarUrl
        useAvatar
      }
    }
  }
`;
const MANAGE_CONNECTION = gql`
  mutation ManageConnection(
    $connectionId: ID!
    $isApproved: Boolean!
    $isBlocked: Boolean!
  ) {
    manageConnections(
      connection: {
        connectionId: $connectionId
        isApproved: $isApproved
        isBlocked: $isBlocked
      }
    ) {
      success
      errors
      connection {
        uuid
        isApproved
        isBlocked
      }
    }
  }
`;
const MANAGE_PUSH_SUBSCRIPTION = gql`
  mutation ManagePushSubscriptionMutation(
    $unsubscribe: Boolean!
    $p256dh: String!
    $endpoint: String!
    $auth: String!
  ) {
    managePushSubscription(
      endpoint: $endpoint
      auth: $auth
      unsubscribe: $unsubscribe
      p256dh: $p256dh
    ) {
      success
      errors
    }
  }
`;
export {
  START_CONVERSATION,
  JOIN_CONVERSATION,
  NEW_CONVERSATION_MESSAGE,
  UPDATE_USER_CONVERSATION,
  FILE_UPLOAD,
  NEW_CHAT_MESSAGE,
  UPDATE_LIVE_CALL_INVITATION,
  ATTEND_LIVE_CALL,
  MANAGE_PUSH_SUBSCRIPTION,
  MANAGE_CONNECTION,
};
